import { ArrowExportRegular } from "@fluentui/react-icons";
import { ManageAttachmentsDialog } from "components/ManageAttachmentsDialog/ManageAttachmentsDialog";
import { ActionButton, Flex } from "components/styled";
import {
  Attachment,
  IFormattedFile,
  IFormattedFileExtended,
  IRowItem,
} from "library/types";
import React, { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useCreateCategoryAttachment } from "state/queries/useCreateCategoryAttachment";
import { useDeleteBenefitCategoryAttachment } from "state/queries/useDeleteBenefitCategoryAttachment";
import { useUpdateCategoryAttachment } from "state/queries/useUpdateCategoryAttachment";

type Props = {
  item: IRowItem;
  disabled?: boolean;
};

export function ManageCategoryDocuments({ item, disabled }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = React.useState(false);

  const onDialogOpenChange = useCallback((state) => {
    setOpen(state);
  }, []);

  const {
    mutateAsync: onCreateAttachment,
    isLoading: createAttachmentInProgress,
  } = useCreateCategoryAttachment();

  const { mutate: onUpdateAttachment, isLoading: updateInProgress } =
    useUpdateCategoryAttachment();

  const { mutate: onDeleteAttachment, isLoading: deleteInProgress } =
    useDeleteBenefitCategoryAttachment();

  const handleCreateAttachment = useCallback(
    (
      newAttach: IFormattedFile | null,
      isHiddenForEmployee?: boolean,
      newAttachList?: IFormattedFileExtended[],
      onSuccessFn?: () => void
    ) => {
      try {
        if (newAttachList) {
          const promiseArray = [] as Promise<any>[];
          newAttachList.forEach((attachment) => {
            promiseArray.push(
              onCreateAttachment({
                benefitCategoryTag: Number(item?.benefitCategoryTag),
                clientInternalNumberGOS: item?.clientInternalNumberGos || 0,
                countryCode: item?.countryCode || 0,
                payload: {
                  attachmentName: attachment?.name || "",
                  attachmentContent: attachment?.base64String || "",
                  attachmentMimeType: attachment?.type || "",
                  isHiddenForEmployee,
                },
              })
            );
          });

          Promise.all(promiseArray).then(() => {
            toast.success(t("hbh.documentCreatedSuccessfully.label"));
            onSuccessFn && onSuccessFn();
          });
          return;
        }

        onCreateAttachment(
          {
            benefitCategoryTag: Number(item.benefitCategoryTag),
            clientInternalNumberGOS: item.clientInternalNumberGos || 0,
            countryCode: item.countryCode || 0,
            payload: {
              attachmentName: newAttach?.name || "",
              attachmentContent: newAttach?.base64String || "",
              attachmentMimeType: newAttach?.type || "",
              isHiddenForEmployee,
              // rowVersion: item.rowVersion || "",
            },
          },
          {
            onSuccess: () => {
              onSuccessFn && onSuccessFn();

              toast.success(t("hbh.documentCreatedSuccessfully.label"));
            },
          }
        );
      } catch (error) {
        console.log(error, "error in manage category documents");
      }
    },
    []
  );

  const handleUpdateAttachment = useCallback(
    ({
      attachment,
      newValues,
    }: {
      attachment: Attachment;
      newValues: {
        attachmentName: string;
        attachmentSummary: string;
        attachmentAbstract: string;
      };
    }) => {
      onUpdateAttachment(
        {
          attachmentName: attachment.attachmentName || "",
          benefitCategoryTag: Number(item.benefitCategoryTag),
          clientInternalNumberGOS: item.clientInternalNumberGos || 0,
          countryCode: item.countryCode || 0,
          payload: {
            ...attachment,
            // rowVersion: item.rowVersion || "",
            attachmentName: newValues?.attachmentName,
            attachmentSummary: newValues?.attachmentSummary,
            attachmentAbstract: newValues?.attachmentAbstract,
            isHiddenForEmployee: true,
          },
        },
        {
          onSuccess: () => {
            toast.success(t("hbh.documentUpdatedSuccessfully.label"));
          },
        }
      );
    },
    []
  );

  const handleDeleteAttachment = useCallback((attach: Attachment) => {
    onDeleteAttachment(
      {
        attachmentName: attach.attachmentName || "",
        benefitCategoryTag: Number(item.benefitCategoryTag),
        clientInternalNumberGOS: item.clientInternalNumberGos || 0,
        countryCode: item.countryCode || 0,
        rowVersion: attach.rowVersion || "",
      },
      {
        onSuccess: () => {
          toast.success(t("hbh.documentDeletedSuccessfully.label"));
        },
      }
    );
  }, []);

  const attachNumber = item?.attachments?.length;
  const titleLabel = attachNumber
    ? t("hbh.manageDocuments.label")
    : t("hbh.addDocuments.label");

  return (
    <>
      <Flex justify="flex-start" gap={"5px"}>
        <ActionButton
          disabled={disabled}
          icon={<ArrowExportRegular />}
          noBorder
          appearance="subtle"
          onClick={(e) => {
            setOpen(true);
          }}
        >
          {titleLabel}
          {attachNumber
            ? ` (${attachNumber} ${t("hbh.items.label")})`
            : ` (${t("hbh.noData.label")}) `}
        </ActionButton>
      </Flex>
      {open && (
        <ManageAttachmentsDialog
          showIsAttachHiddenForEmployeeField={false}
          attachmentActionsState={{
            createAttachmentInProgress,
            onCreateAttachment: handleCreateAttachment,
            updateInProgress,
            onUpdateAttachment: handleUpdateAttachment,
            deleteInProgress: false,
            onDeleteAttachment: handleDeleteAttachment,
          }}
          item={item}
          open={open}
          setOpen={onDialogOpenChange}
        />
      )}
    </>
  );
}
